/* Nurly — fiche produit : galerie + sélecteur de pack + ajout au panier (multi-produits) */
import { CartUpdatedEvent } from '@theme/events';

/* ---------------- Galerie ---------------- */
class NurlyGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('.np-gallery__slide')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dialog = this.querySelector('[data-zoom-dialog]');
    if (!this.track) return;

    this.querySelectorAll('[data-dir]').forEach((btn) =>
      btn.addEventListener('click', () => this.go(this.index() + parseInt(btn.dataset.dir, 10))),
    );
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.thumb, 10))));

    let raf = null;
    this.track.addEventListener(
      'scroll',
      () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.setActive(this.index()));
      },
      { passive: true },
    );

    // Zoom plein écran
    if (this.dialog) {
      this.querySelectorAll('[data-zoom]').forEach((b) =>
        b.addEventListener('click', () => {
          if (typeof this.dialog.showModal !== 'function') return;
          this.dialog.showModal();
          document.documentElement.style.overflow = 'hidden';
          const target = this.dialog.querySelector(`[data-zoom-item="${b.dataset.zoom}"]`);
          if (target) target.scrollIntoView({ block: 'start' });
        }),
      );
      const close = () => {
        this.dialog.close();
      };
      this.dialog.querySelector('[data-zoom-close]')?.addEventListener('click', close);
      this.dialog.addEventListener('close', () => (document.documentElement.style.overflow = ''));
    }
  }

  // pas d'une image = largeur + écart entre images (24px PC / 3px mobile, comme le live)
  step() {
    const s = this.slides;
    return (s[1] ? s[1].offsetLeft - s[0].offsetLeft : this.track.clientWidth) || 1;
  }

  index() {
    return Math.round(this.track.scrollLeft / this.step());
  }

  go(i) {
    const n = this.slides.length;
    if (!n) return;
    const idx = (i + n) % n;
    this.track.scrollTo({ left: idx * this.step(), behavior: 'smooth' });
    this.setActive(idx);
  }

  setActive(i) {
    this.thumbs.forEach((t, k) => {
      const on = k === i;
      t.classList.toggle('is-active', on);
      if (on) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  }
}
if (!customElements.get('nurly-gallery')) customElements.define('nurly-gallery', NurlyGallery);

/* ---------------- Packs + panier ---------------- */
class NurlyBuyBox extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-buy-form]');
    this.button = this.querySelector('[data-atc]');
    this.error = this.querySelector('[data-error]');
    this.packs = [...this.querySelectorAll('[data-pack]')];

    this.packs.forEach((p) => {
      const input = p.querySelector('input[type="radio"]');
      input?.addEventListener('change', () => this.select(p));
    });
    this.form?.addEventListener('submit', (e) => this.submit(e));
  }

  select(pack) {
    this.packs.forEach((p) => p.classList.toggle('is-selected', p === pack));
  }

  selectedIds() {
    const sel = this.packs.find((p) => p.querySelector('input[type="radio"]')?.checked);
    if (sel && sel.dataset.ids) {
      return sel.dataset.ids
        .split(',')
        .map((s) => s.trim())
        .filter((s) => /^\d+$/.test(s));
    }
    const id = this.form.querySelector('input[name="id"]')?.value;
    return id && /^\d+$/.test(id) ? [id] : [];
  }

  async submit(event) {
    event.preventDefault();
    if (!this.button || this.button.disabled) return;
    const ids = this.selectedIds();
    if (!ids.length) return;

    // une ligne par variante (quantités cumulées)
    const map = new Map();
    ids.forEach((id) => map.set(id, (map.get(id) || 0) + 1));
    const items = [...map].map(([id, quantity]) => ({ id: Number(id), quantity }));
    const added = items.reduce((a, i) => a + i.quantity, 0);

    const sections = ['auto-gifts'];
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.dataset.sectionId) sections.push(drawer.dataset.sectionId);

    this.setLoading(true);
    this.showError('');
    try {
      const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        this.showError(data.description || data.message || 'Impossible d’ajouter au panier.');
        return;
      }
      const icon = document.querySelector('cart-icon');
      const current = icon && typeof icon.getCurrentItemCount === 'function' ? icon.getCurrentItemCount() : null;
      const itemCount = Number.isFinite(current) ? current + added : undefined;
      document.dispatchEvent(new CartUpdatedEvent({}, { itemCount, source: 'product-form', sections: data.sections }));
    } catch (e) {
      this.showError('Impossible d’ajouter au panier. Réessaie dans un instant.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(on) {
    this.button.classList.toggle('is-loading', on);
    this.button.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  showError(msg) {
    if (!this.error) return;
    this.error.textContent = msg;
    this.error.hidden = !msg;
  }
}
if (!customElements.get('nurly-buy-box')) customElements.define('nurly-buy-box', NurlyBuyBox);

/* ---------------- Apparition au défilement (reproduit l'animation des sections du live) ---------------- */
(() => {
  if (window.Shopify && window.Shopify.designMode) return;
  if (!('IntersectionObserver' in window)) return;
  const els = [...document.querySelectorAll('.np-reveal')];
  if (!els.length) return;
  document.documentElement.classList.add('np-reveal-on');
  const show = (el) => {
    el.classList.add('is-in');
    io.unobserve(el);
  };
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      // visible, ou déjà dépassée (saut d'ancre / défilement rapide) : on affiche aussi toutes les sections précédentes
      if (e.isIntersecting || e.boundingClientRect.bottom < 0) {
        const i = els.indexOf(e.target);
        els.slice(0, i + 1).forEach((el) => el.classList.contains('is-in') || show(el));
      }
    });
  });
  els.forEach((el) => io.observe(el));
})();
